import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    CircularProgress,
    Alert,
    AlertTitle,
    Typography,
    Chip,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';

const PLAN_OPTIONS = [
    { value: 'starter', label: 'Starter' },
    { value: 'pro', label: 'Pro' },
    { value: 'enterprise', label: 'Enterprise' },
];

const InviteOrganizationDialog = ({
    open,
    onClose,
    onSubmit,
    colors,
    t,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [email, setEmail] = useState('');
    const [plan, setPlan] = useState('starter');
    const [lang, setLang] = useState('fr');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [existingUserInfo, setExistingUserInfo] = useState(null);

    useEffect(() => {
        if (!open) {
            setEmail('');
            setPlan('starter');
            setLang('fr');
            setError('');
            setExistingUserInfo(null);
        }
    }, [open]);

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

    const handleSubmit = async () => {
        if (!validateEmail(email)) {
            setError(t('invalid_email', 'Please enter a valid email address'));
            return;
        }

        setLoading(true);
        setError('');
        setExistingUserInfo(null);
        try {
            await onSubmit({
                email,
                plan,
                billing_cycle: 'monthly',
                lang,
            });
        } catch (err) {
            if (err.existingUser) {
                setExistingUserInfo(err.existingUser);
            } else {
                setError(err.message || 'Error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !loading && email) {
            handleSubmit();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : '16px',
                    border: fullScreen ? 'none' : `1px solid ${colors.border}`,
                    boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
                {t('invite_organization', 'Invite Organization')}
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 2 }}>
                <DialogContentText sx={{ mb: 3, color: colors.textSecondary }}>
                    {t('invite_org_description', 'Create a new organization and invite its owner. They will receive an email to set up their account and start a 7-day free trial.')}
                </DialogContentText>

                {existingUserInfo && (
                    <Alert severity="warning" sx={{ mb: 2.5, borderRadius: '8px' }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>
                            {t('user_already_exists', 'User already exists')}
                        </AlertTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2">
                                <strong>{t('email', 'Email')}:</strong> {existingUserInfo.email}
                            </Typography>
                            {(existingUserInfo.firstName || existingUserInfo.lastName) && (
                                <Typography variant="body2">
                                    <strong>{t('name', 'Name')}:</strong> {[existingUserInfo.firstName, existingUserInfo.lastName].filter(Boolean).join(' ')}
                                </Typography>
                            )}
                            {existingUserInfo.organizationName && (
                                <Typography variant="body2">
                                    <strong>{t('organization', 'Organization')}:</strong> {existingUserInfo.organizationName}
                                </Typography>
                            )}
                            {existingUserInfo.subscriptionPlan && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="body2">
                                        <strong>{t('plan', 'Plan')}:</strong> {existingUserInfo.subscriptionPlan}
                                    </Typography>
                                    {existingUserInfo.subscriptionStatus && (
                                        <Chip
                                            label={existingUserInfo.subscriptionStatus}
                                            size="small"
                                            color={
                                                existingUserInfo.subscriptionStatus === 'active' ? 'success' :
                                                existingUserInfo.subscriptionStatus === 'trial' ? 'info' :
                                                existingUserInfo.subscriptionStatus === 'cancelled' ? 'error' : 'default'
                                            }
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Alert>
                )}

                <TextField
                    autoFocus
                    label={t('email_address', 'Email Address')}
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    onKeyDown={handleKeyDown}
                    error={!!error}
                    helperText={error}
                    sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel>{t('plan', 'Plan')}</InputLabel>
                        <Select
                            value={plan}
                            label={t('plan', 'Plan')}
                            onChange={(e) => setPlan(e.target.value)}
                            sx={{ borderRadius: '8px' }}
                        >
                            {PLAN_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel>{t('email_language', 'Email Language')}</InputLabel>
                        <Select
                            value={lang}
                            label={t('email_language', 'Email Language')}
                            onChange={(e) => setLang(e.target.value)}
                            sx={{ borderRadius: '8px' }}
                        >
                            <MenuItem value="fr">Français</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                <Button onClick={onClose} sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={!email || loading}
                    sx={{
                        bgcolor: colors.primary, color: 'white', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
                        '&:hover': { bgcolor: colors.primaryDark },
                        '&.Mui-disabled': { bgcolor: alpha(colors.primary, 0.6), color: 'white' },
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            {t('sending', 'Sending...')}
                        </Box>
                    ) : (
                        t('send_invitation', 'Send Invitation')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InviteOrganizationDialog;
